import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { ChangeEvent, memo, TransitionEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import HeadsetIcon from '@/assets/icons/headset-icon.svg?react';
import { PreloadAttachmentsResponse } from '@/modules/api/types';
import {
  Input,
  MESSAGE_LIMIT,
  PromptButton,
  SamTransferModal,
  SendButton,
  UploadButton,
  UploadPreview,
  useKeyboard,
  useTheme,
} from '@/modules/common';
import { useBehaviour } from '@/modules/context';
import { Maintenance } from '@/modules/maintenance/Maintenance';
import { Messages, MessagesProps } from '@/modules/messages';
import {
  attachmentsAtom,
  bridgeAtom,
  chatFeedbackAtom,
  ChatWindowHeights,
  chatWindowHeightsAtom,
  errorAtom,
  isLiveAgentAvailableAtom,
  isLiveAgentModeAtom,
  isLoadingMessageAtom,
  isMlAnsweredAtom,
  isOpenChatAtom,
  isReadyForScrollAtom,
  isSemiOpenChatAtom,
  isUserAnsweredAtom,
  messageTextAtom,
  transferToLiveAgentAtom,
  useAtom,
  useSetValue,
  useValue,
} from '@/store';
import {
  CloseButton,
  CloseButtonContainer,
  Footer,
  Header,
  IconContainer,
  InputContainer,
  LiveAgentButton,
  LiveAgentIconWrapper,
  LiveAgentText,
  PoweredBy,
  PromtsContainer,
  Star,
  Wrapper,
} from './Content.styles';

export type ContentProps = MessagesProps & {
  onChatWindowClick?: () => void;
};

export const Content = memo<ContentProps>((props) => {
  const { sendMessage, resendMessage, removeMessage, onChatWindowClick } = props;

  const theme = useTheme();
  const isMobile = window.matchMedia(`(${theme.mediaMobile})`).matches;

  const isOpenChat = useValue(isOpenChatAtom);
  const { translations, isEmbedded, prompts, isLiveAgentEnabled, isUploadBtnActive = true } = useValue(bridgeAtom);
  const chatFeedback = useValue(chatFeedbackAtom);
  const isLoadingMessage = useValue(isLoadingMessageAtom);
  const error = useValue(errorAtom);
  const isMlAnswered = useValue(isMlAnsweredAtom);
  const isUserAnswered = useValue(isUserAnsweredAtom);

  const isLiveAgentMode = useValue(isLiveAgentModeAtom);
  const isLiveAgentAvailable = useValue(isLiveAgentAvailableAtom);
  const transferToLiveAgent = useValue(transferToLiveAgentAtom);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);

  const isSemiOpenChat = useValue(isSemiOpenChatAtom);

  const [messageText, setMessageText] = useAtom(messageTextAtom);
  const [attachments, setAttachments] = useAtom(attachmentsAtom);
  const [isReadyForScroll, setIsReadyForScroll] = useAtom(isReadyForScrollAtom);
  const setChatWindowHeights = useSetValue(chatWindowHeightsAtom);

  const { toggleChat } = useBehaviour();
  const { isOpenKeyboard } = useKeyboard();

  const [isFirstRender, setIsFirstRender] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<HTMLUListElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const maintenanceRef = useRef<HTMLDivElement>(null);

  const onChangeInput = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setMessageText(e.target.value);
    },
    [setMessageText]
  );

  const onTransitionEnd = useCallback(
    (event: TransitionEvent<HTMLDivElement>) => {
      if (!isMlAnswered || isReadyForScroll || event.propertyName !== 'height') {
        return;
      }

      setIsReadyForScroll(true);
    },
    [isMlAnswered, isReadyForScroll, setIsReadyForScroll]
  );

  const onFilesSelectedHandler = useCallback(
    (newAttachments: PreloadAttachmentsResponse) => {
      setAttachments((prevAttachments) => [...prevAttachments, ...newAttachments]);
    },
    [setAttachments]
  );

  const onRemoveAttachmentHandler = useCallback(
    (index: number) => {
      setAttachments((prevAttachments) => prevAttachments.filter((_, i) => i !== index));
    },
    [setAttachments]
  );

  useEffect(() => {
    const onResize = () => {
      setIsFirstRender(true);
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const container = contentRef.current;
    const messages = messagesRef.current;

    if (!container || !messages) {
      return;
    }

    const handleTouchMove = (e: TouchEvent) => {
      const target = e.target as Node;
      const canScroll = messages.scrollHeight > messages.clientHeight;

      if (canScroll && messages.contains(target)) {
        return;
      }

      e.preventDefault();
    };

    container.addEventListener('touchmove', handleTouchMove, {
      passive: false,
    });

    return () => {
      container.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  useEffect(() => {
    if (!isMlAnswered || isReadyForScroll || (!isMobile && !isEmbedded)) {
      return;
    }

    setIsReadyForScroll(true);
  }, [isMlAnswered, isReadyForScroll, setIsReadyForScroll, isMobile, isEmbedded]);

  // effect that retrieves UI elements heights
  useLayoutEffect(() => {
    const newHeights: Partial<ChatWindowHeights> = {};
    if (headerRef.current) {
      newHeights.header = headerRef.current.getBoundingClientRect().height;
    }
    if (footerRef.current) {
      newHeights.footer = footerRef.current.getBoundingClientRect().height;
    }
    if (maintenanceRef.current) {
      newHeights.maintenance = maintenanceRef.current.getBoundingClientRect().height;
    }
    if (messagesRef.current) {
      const styles = getComputedStyle(messagesRef.current);
      newHeights.messageList =
        parseFloat(styles.paddingTop) +
        parseFloat(styles.paddingBottom) +
        parseFloat(styles.marginTop) +
        parseFloat(styles.marginBottom);
    }
    setChatWindowHeights((prev) => ({ ...prev, ...newHeights }));
  }, [setChatWindowHeights]);

  // effect to get 1st chat message height
  useEffect(() => {
    const listEl = messagesRef.current;
    if (!listEl || !isSemiOpenChat) {
      return;
    }

    let resizeObserver: ResizeObserver | null = null;
    let scrollTimer: ReturnType<typeof setTimeout> | null = null;
    const SCROLL_DEBOUNCE_MS = 300;

    const debouncedScrollToFirstMessage = () => {
      if (scrollTimer) {
        clearTimeout(scrollTimer);
      }
      scrollTimer = setTimeout(() => {
        const messageEl = listEl.querySelector('#first-message');
        if (!messageEl || !messagesRef.current) {
          return;
        }
        let top = messageEl.getBoundingClientRect().top;
        if (theme.isRtl) {
          top = -top;
        }
        messagesRef.current.scrollTo({
          top,
          behavior: 'smooth',
        });
      }, SCROLL_DEBOUNCE_MS);
    };

    const mutationObserver = new MutationObserver(() => {
      const message = listEl.querySelector('#first-message');
      if (!message) {
        return;
      }

      if (!resizeObserver) {
        setChatWindowHeights((prev) => ({
          ...prev,
          firstMessage: message.getBoundingClientRect().height,
        }));

        resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.target.getBoundingClientRect().height;
            setChatWindowHeights((prev) => ({ ...prev, firstMessage: height }));
          }
          debouncedScrollToFirstMessage();
        });
        resizeObserver.observe(message);
      }

      debouncedScrollToFirstMessage();
    });
    mutationObserver.observe(listEl, { childList: true });

    return () => {
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
      if (scrollTimer) {
        clearTimeout(scrollTimer);
      }
    };
  }, [setChatWindowHeights, isSemiOpenChat, theme.isRtl]);

  if (isOpenChat && isFirstRender) {
    setIsFirstRender(false);
  }

  const hasChatFeedback = Boolean(chatFeedback);

  const disableSendButton = isLoadingMessage || hasChatFeedback || (!messageText && !attachments.length);
  const isDisabled = isLoadingMessage || error !== null;

  if (isEmbedded) {
    return (
      <Wrapper
        ref={contentRef}
        isOpen={isOpenChat}
        isOpenKeyboard={isOpenKeyboard}
        isFirstRender={isFirstRender}
        isEmbedded={isEmbedded}
        data-lenis-prevent
      >
        <Messages
          sendMessage={sendMessage}
          resendMessage={resendMessage}
          removeMessage={removeMessage}
          ref={messagesRef}
        />

        <Footer hasChatFeedback={hasChatFeedback}>
          <SamTransferModal isOpen={isTransferModalOpen} onClose={() => setIsTransferModalOpen(false)} />
          <InputContainer>
            {isUploadBtnActive && !isLiveAgentMode && (
              <UploadButton
                disabled={error !== null || attachments.length >= 5}
                onFileSelect={onFilesSelectedHandler}
              />
            )}
            <Input
              value={messageText}
              maxLength={MESSAGE_LIMIT}
              disabled={error !== null}
              focus={messageText.length === 0}
              placeholder={window.LLL(translations.inputPlaceholder)}
              onSend={!isLoadingMessage ? sendMessage : () => {}}
              onChange={onChangeInput}
              hasAttachmentsButton={isUploadBtnActive && !isLiveAgentMode}
            />
            <SendButton
              title={window.LLL(translations.sendMessageButton)}
              disabled={disableSendButton}
              onClick={sendMessage}
              isEmbedded
            />
          </InputContainer>

          <UploadPreview attachments={attachments} onRemove={onRemoveAttachmentHandler} />

          <PromtsContainer>
            {prompts?.map(({ caption, prompt, iconUrl, iconSvg, translationId }, index) => {
              const promptTranslation = translations.prompts?.[translationId];
              const captionText = promptTranslation?.caption ? window.LLL(promptTranslation.caption) : caption;
              const promptText = promptTranslation?.prompt ? window.LLL(promptTranslation.prompt) : prompt;

              return (
                <PromptButton
                  key={index}
                  icon={iconUrl}
                  iconSvg={iconSvg}
                  disabled={isDisabled}
                  prompt={promptText || captionText}
                >
                  {captionText}
                </PromptButton>
              );
            })}
          </PromtsContainer>

          <PoweredBy>
            <span>{`${window.LLL(translations.poweredBy)} `}</span>
            <a href="https://saywhatt.com/" target="_blank" rel="noreferrer">
              <b>saywhatt</b>
            </a>
            <a
              id="privacy-link"
              href="https://saywhatt.com/service-privacy-policy"
              target="_blank"
              rel="noreferrer"
            >{`${window.LLL(translations.privacyLink)} `}</a>
          </PoweredBy>
        </Footer>
      </Wrapper>
    );
  }

  return (
    <Wrapper
      ref={contentRef}
      isOpen={isOpenChat}
      isOpenKeyboard={isOpenKeyboard}
      isFirstRender={isFirstRender}
      isMlAnswered={isMlAnswered}
      onTransitionEnd={onTransitionEnd}
      data-testid="saywhatt-chat-window-wrapper"
      id="saywhatt-chat-window-wrapper"
      data-lenis-prevent
      onClick={() => onChatWindowClick?.()}
    >
      <Header ref={headerRef}>
        <IconContainer isRtl={theme.isRtl}>
          <Star size={16} />
          <Star size={12} />
        </IconContainer>

        <p data-testid="saywhatt-chat-window-title">
          {parse(DOMPurify.sanitize(window.LLL(translations.title), { USE_PROFILES: { html: true } }))}
        </p>

        {isLiveAgentEnabled && isLiveAgentAvailable && (
          <LiveAgentButton
            isLive={isLiveAgentMode}
            disabled={isLoadingMessage}
            onClick={() => (isLiveAgentMode ? setIsTransferModalOpen(true) : transferToLiveAgent?.())}
          >
            <LiveAgentIconWrapper>
              <HeadsetIcon />
            </LiveAgentIconWrapper>
            <LiveAgentText>{isLiveAgentMode ? 'End Conversation' : 'Live Agent'}</LiveAgentText>
          </LiveAgentButton>
        )}

        <CloseButtonContainer
          isRtl={theme.isRtl}
          onClick={(e) => {
            e.stopPropagation();
            toggleChat({ isOpenChat: false });
          }}
          role="button"
          aria-label="close button"
          data-testid="saywhatt-chat-window-close"
        >
          <CloseButton />
        </CloseButtonContainer>
      </Header>

      <Messages
        sendMessage={sendMessage}
        resendMessage={resendMessage}
        removeMessage={removeMessage}
        ref={messagesRef}
      />

      <Footer hasChatFeedback={hasChatFeedback} ref={footerRef}>
        <SamTransferModal isOpen={isTransferModalOpen} onClose={() => setIsTransferModalOpen(false)} />
        <InputContainer>
          {isUploadBtnActive && !isLiveAgentMode && (
            <UploadButton disabled={error !== null || attachments.length >= 5} onFileSelect={onFilesSelectedHandler} />
          )}
          <Input
            value={messageText}
            maxLength={MESSAGE_LIMIT}
            disabled={error !== null}
            focus={messageText.length === 0}
            placeholder={window.LLL(translations.inputPlaceholder)}
            onSend={!isLoadingMessage ? sendMessage : () => {}}
            onChange={onChangeInput}
            hasAttachmentsButton={isUploadBtnActive && !isLiveAgentMode}
          />
          <SendButton
            title={window.LLL(translations.sendMessageButton)}
            disabled={disableSendButton}
            onClick={sendMessage}
          />
        </InputContainer>

        <UploadPreview attachments={attachments} onRemove={onRemoveAttachmentHandler} />

        {!isUserAnswered && (
          <PromtsContainer data-testid="saywhatt-chat-prompt-list">
            {prompts?.map(({ caption, prompt, iconUrl, iconSvg, translationId }, index) => {
              const promptTranslation = translations.prompts?.[translationId];
              const captionText = promptTranslation?.caption ? window.LLL(promptTranslation.caption) : caption;
              const promptText = promptTranslation?.prompt ? window.LLL(promptTranslation.prompt) : prompt;

              return (
                <PromptButton
                  icon={iconUrl}
                  iconSvg={iconSvg}
                  disabled={isDisabled}
                  prompt={promptText || captionText}
                  key={index}
                >
                  {captionText}
                </PromptButton>
              );
            })}
          </PromtsContainer>
        )}

        <PoweredBy>
          <span id="powered-by">{`${window.LLL(translations.poweredBy)} `}</span>
          <a href="https://saywhatt.com/" target="_blank" rel="noreferrer">
            <b>saywhatt</b>
          </a>
          <a
            id="privacy-link"
            href="https://saywhatt.com/service-privacy-policy"
            target="_blank"
            rel="noreferrer"
          >{`${window.LLL(translations.privacyLink)} `}</a>
        </PoweredBy>
      </Footer>

      <Maintenance ref={maintenanceRef} />
    </Wrapper>
  );
});
